import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  Building2, 
  DollarSign, 
  Target, 
  Users, 
  BarChart3,
  FileText,
  Shield,
  Loader2,
  Save,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const sdgOptions = [
  { value: 1, label: 'SDG 1: No Poverty' },
  { value: 2, label: 'SDG 2: Zero Hunger' },
  { value: 3, label: 'SDG 3: Good Health' },
  { value: 4, label: 'SDG 4: Quality Education' },
  { value: 5, label: 'SDG 5: Gender Equality' },
  { value: 6, label: 'SDG 6: Clean Water' },
  { value: 7, label: 'SDG 7: Affordable Energy' },
  { value: 8, label: 'SDG 8: Decent Work' },
  { value: 9, label: 'SDG 9: Industry & Innovation' },
  { value: 10, label: 'SDG 10: Reduced Inequalities' },
  { value: 11, label: 'SDG 11: Sustainable Cities' },
  { value: 12, label: 'SDG 12: Responsible Consumption' },
  { value: 13, label: 'SDG 13: Climate Action' },
  { value: 14, label: 'SDG 14: Life Below Water' },
  { value: 15, label: 'SDG 15: Life on Land' },
  { value: 16, label: 'SDG 16: Peace & Justice' },
  { value: 17, label: 'SDG 17: Partnerships' }
];

const InvestorForm = ({ investor, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const isEdit = !!investor;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    // Basic Info
    investor_type: investor?.investor_type || '',
    organization_name: investor?.organization_name || '',
    email: investor?.email || '',
    phone: investor?.phone || '',
    website: investor?.website || '',
    address: investor?.address || '',
    city: investor?.city || '',
    country: investor?.country || 'Greece',
    legal_form: investor?.legal_form || '',
    year_established: investor?.year_established || '',
    
    // Investment Profile
    investment_strategy: investor?.investment_strategy || '',
    capital_available: investor?.capital_available || '',
    capital_origin: investor?.capital_origin || '',
    time_horizon: investor?.time_horizon || '',
    expected_return: investor?.expected_return || '',
    risk_appetite: investor?.risk_appetite || '',
    minimum_investment_size: investor?.minimum_investment_size || '',
    maximum_investment_size: investor?.maximum_investment_size || '',
    geographic_focus: investor?.geographic_focus || [],
    
    // SDG & Mission
    sdg_alignment: investor?.sdg_alignment || [],
    primary_sdgs: investor?.primary_sdgs || [],
    social_mission: investor?.social_mission || '',
    environmental_mission: investor?.environmental_mission || '',
    csr_policy: investor?.csr_policy || '',
    
    // Non-Financial
    mentoring_hours_annual: investor?.mentoring_hours_annual || '',
    volunteering_hours_annual: investor?.volunteering_hours_annual || '',
    governance_participation: investor?.governance_participation || false,
    expertise_areas: investor?.expertise_areas || [],
    
    // Impact Management
    has_impact_measurement_system: investor?.has_impact_measurement_system || false,
    has_theory_of_change: investor?.has_theory_of_change || false,
    publishes_impact_reports: investor?.publishes_impact_reports || false,
    third_party_verification: investor?.third_party_verification || false,
    
    // ESG
    has_esg_policy: investor?.has_esg_policy || false,
    signed_pri: investor?.signed_pri || false,
    publishes_esg_reports: investor?.publishes_esg_reports || false,
    
    // Historical
    previous_investments_count: investor?.previous_investments_count || '',
    total_capital_deployed: investor?.total_capital_deployed || '',
    investment_period_years: investor?.investment_period_years || '',
    beneficiaries_reached: investor?.beneficiaries_reached || '',
    jobs_created: investor?.jobs_created || '',
    
    // Transparency
    willing_to_share_data: investor?.willing_to_share_data || false,
    annual_reporting: investor?.annual_reporting || false,
    
    // Platform
    is_public: investor?.is_public || false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSDGToggle = (sdg) => {
    setFormData(prev => {
      const current = prev.sdg_alignment || [];
      if (current.includes(sdg)) {
        return { ...prev, sdg_alignment: current.filter(s => s !== sdg) };
      } else {
        return { ...prev, sdg_alignment: [...current, sdg] };
      }
    });
  };

  const handleGeographicToggle = (geo) => {
    setFormData(prev => {
      const current = prev.geographic_focus || [];
      if (current.includes(geo)) {
        return { ...prev, geographic_focus: current.filter(g => g !== geo) };
      } else {
        return { ...prev, geographic_focus: [...current, geo] };
      }
    });
  };

  const handleExpertiseToggle = (exp) => {
    setFormData(prev => {
      const current = prev.expertise_areas || [];
      if (current.includes(exp)) {
        return { ...prev, expertise_areas: current.filter(e => e !== exp) };
      } else {
        return { ...prev, expertise_areas: [...current, exp] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.investor_type || !formData.organization_name || !formData.email) {
      toast.error(t('investor_form.fill_required'));
      return;
    }

    setLoading(true);
    try {
      // Clean up numeric fields
      const cleanedData = {
        ...formData,
        capital_available: formData.capital_available ? parseFloat(formData.capital_available) : null,
        minimum_investment_size: formData.minimum_investment_size ? parseFloat(formData.minimum_investment_size) : null,
        maximum_investment_size: formData.maximum_investment_size ? parseFloat(formData.maximum_investment_size) : null,
        total_capital_deployed: formData.total_capital_deployed ? parseFloat(formData.total_capital_deployed) : null,
        year_established: formData.year_established ? parseInt(formData.year_established) : null,
        mentoring_hours_annual: formData.mentoring_hours_annual ? parseInt(formData.mentoring_hours_annual) : null,
        volunteering_hours_annual: formData.volunteering_hours_annual ? parseInt(formData.volunteering_hours_annual) : null,
        previous_investments_count: formData.previous_investments_count ? parseInt(formData.previous_investments_count) : null,
        investment_period_years: formData.investment_period_years ? parseInt(formData.investment_period_years) : null,
        beneficiaries_reached: formData.beneficiaries_reached ? parseInt(formData.beneficiaries_reached) : null,
        jobs_created: formData.jobs_created ? parseInt(formData.jobs_created) : null
      };

      if (isEdit) {
        await axios.patch(`${API}/api/social-investors/${investor.id}`, cleanedData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        await axios.post(`${API}/api/social-investors`, cleanedData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
      
      onSuccess();
    } catch (error) {
      console.error('Failed to save investor:', error);
      toast.error(error.response?.data?.detail || t('investor_form.save_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-7">
          <TabsTrigger value="basic">
            <Building2 className="h-4 w-4 mr-1 hidden sm:inline" />
            {t('investor_form.tab_basic')}
          </TabsTrigger>
          <TabsTrigger value="investment">
            <DollarSign className="h-4 w-4 mr-1 hidden sm:inline" />
            {t('investor_form.tab_investment')}
          </TabsTrigger>
          <TabsTrigger value="sdg">
            <Target className="h-4 w-4 mr-1 hidden sm:inline" />
            {t('investor_form.tab_sdg')}
          </TabsTrigger>
          <TabsTrigger value="support">
            <Users className="h-4 w-4 mr-1 hidden sm:inline" />
            {t('investor_form.tab_support')}
          </TabsTrigger>
          <TabsTrigger value="impact">
            <BarChart3 className="h-4 w-4 mr-1 hidden sm:inline" />
            {t('investor_form.tab_impact')}
          </TabsTrigger>
          <TabsTrigger value="esg">
            <Shield className="h-4 w-4 mr-1 hidden sm:inline" />
            {t('investor_form.tab_esg')}
          </TabsTrigger>
          <TabsTrigger value="history">
            <FileText className="h-4 w-4 mr-1 hidden sm:inline" />
            {t('investor_form.tab_history')}
          </TabsTrigger>
        </TabsList>

        {/* Basic Info Tab */}
        <TabsContent value="basic" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('investor_form.investor_type')} *</Label>
              <Select value={formData.investor_type} onValueChange={(v) => handleChange('investor_type', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('investor_form.select_investor_type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="investment_fund">Investment Fund</SelectItem>
                  <SelectItem value="philanthropic_foundation">Philanthropic Foundation</SelectItem>
                  <SelectItem value="family_office">Family Office</SelectItem>
                  <SelectItem value="individual">Individual Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.organization_name')} *</Label>
              <Input
                value={formData.organization_name}
                onChange={(e) => handleChange('organization_name', e.target.value)}
                placeholder={t('investor_form.organization_name_placeholder')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.email')} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="contact@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.phone')}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+30..."
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.website')}</Label>
              <Input
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.legal_form')}</Label>
              <Input
                value={formData.legal_form}
                onChange={(e) => handleChange('legal_form', e.target.value)}
                placeholder="e.g., S.A., Ltd., Foundation"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.year_established')}</Label>
              <Input
                type="number"
                value={formData.year_established}
                onChange={(e) => handleChange('year_established', e.target.value)}
                placeholder="2000"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.city')}</Label>
              <Input
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="Athens"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.country')}</Label>
              <Input
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                placeholder="Greece"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.address')}</Label>
              <Input
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder={t('investor_form.address_placeholder')}
              />
            </div>
          </div>
        </TabsContent>

        {/* Investment Profile Tab */}
        <TabsContent value="investment" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('investor_form.capital_available')}</Label>
              <Input
                type="number"
                value={formData.capital_available}
                onChange={(e) => handleChange('capital_available', e.target.value)}
                placeholder="1000000"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.capital_origin')}</Label>
              <Select value={formData.capital_origin} onValueChange={(v) => handleChange('capital_origin', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('investor_form.select_origin')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="own_capital">Own Capital</SelectItem>
                  <SelectItem value="csr_funds">CSR Funds</SelectItem>
                  <SelectItem value="endowments">Endowments</SelectItem>
                  <SelectItem value="third_party_managed">Third-Party Managed</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.minimum_investment')}</Label>
              <Input
                type="number"
                value={formData.minimum_investment_size}
                onChange={(e) => handleChange('minimum_investment_size', e.target.value)}
                placeholder="50000"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.maximum_investment')}</Label>
              <Input
                type="number"
                value={formData.maximum_investment_size}
                onChange={(e) => handleChange('maximum_investment_size', e.target.value)}
                placeholder="500000"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.time_horizon')}</Label>
              <Select value={formData.time_horizon} onValueChange={(v) => handleChange('time_horizon', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('investor_form.select_time_horizon')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short_term">&lt; 1 Year</SelectItem>
                  <SelectItem value="medium_term">1-3 Years</SelectItem>
                  <SelectItem value="long_term">3-7 Years</SelectItem>
                  <SelectItem value="very_long_term">&gt; 7 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.expected_return')}</Label>
              <Select value={formData.expected_return} onValueChange={(v) => handleChange('expected_return', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('investor_form.select_expected_return')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="market_rate">Market Rate</SelectItem>
                  <SelectItem value="below_market">Below Market</SelectItem>
                  <SelectItem value="zero_return">Zero Return (Impact First)</SelectItem>
                  <SelectItem value="capital_preservation">Capital Preservation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.risk_appetite')}</Label>
              <Select value={formData.risk_appetite} onValueChange={(v) => handleChange('risk_appetite', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('investor_form.select_risk_appetite')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="conservative">Conservative</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="aggressive">Aggressive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('investor_form.geographic_focus')}</Label>
            <div className="flex flex-wrap gap-2">
              {['local', 'regional', 'national', 'eu', 'global'].map(geo => (
                <Badge
                  key={geo}
                  variant={formData.geographic_focus?.includes(geo) ? 'default' : 'outline'}
                  className="cursor-pointer capitalize"
                  onClick={() => handleGeographicToggle(geo)}
                >
                  {geo}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('investor_form.investment_strategy')}</Label>
            <Textarea
              value={formData.investment_strategy}
              onChange={(e) => handleChange('investment_strategy', e.target.value)}
              placeholder={t('investor_form.investment_strategy_placeholder')}
              rows={3}
            />
          </div>
        </TabsContent>

        {/* SDG & Mission Tab */}
        <TabsContent value="sdg" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t('investor_form.sdg_alignment_label')}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {sdgOptions.map(sdg => (
                <Badge
                  key={sdg.value}
                  variant={formData.sdg_alignment?.includes(sdg.value) ? 'default' : 'outline'}
                  className="cursor-pointer text-xs py-2 justify-start"
                  onClick={() => handleSDGToggle(sdg.value)}
                >
                  {sdg.label}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('investor_form.social_mission')}</Label>
            <Textarea
              value={formData.social_mission}
              onChange={(e) => handleChange('social_mission', e.target.value)}
              placeholder={t('investor_form.social_mission_placeholder')}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('investor_form.environmental_mission')}</Label>
            <Textarea
              value={formData.environmental_mission}
              onChange={(e) => handleChange('environmental_mission', e.target.value)}
              placeholder={t('investor_form.environmental_mission_placeholder')}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('investor_form.csr_policy')}</Label>
            <Textarea
              value={formData.csr_policy}
              onChange={(e) => handleChange('csr_policy', e.target.value)}
              placeholder={t('investor_form.csr_policy_placeholder')}
              rows={3}
            />
          </div>
        </TabsContent>

        {/* Non-Financial Support Tab */}
        <TabsContent value="support" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('investor_form.mentoring_hours')}</Label>
              <Input
                type="number"
                value={formData.mentoring_hours_annual}
                onChange={(e) => handleChange('mentoring_hours_annual', e.target.value)}
                placeholder="100"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.volunteering_hours')}</Label>
              <Input
                type="number"
                value={formData.volunteering_hours_annual}
                onChange={(e) => handleChange('volunteering_hours_annual', e.target.value)}
                placeholder="500"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="governance"
              checked={formData.governance_participation}
              onCheckedChange={(checked) => handleChange('governance_participation', checked)}
            />
            <Label htmlFor="governance">{t('investor_form.governance_participation')}</Label>
          </div>

          <div className="space-y-2">
            <Label>{t('investor_form.expertise_areas')}</Label>
            <div className="flex flex-wrap gap-2">
              {['marketing', 'it_technology', 'legal', 'finance', 'hr', 'operations', 'strategy', 'sustainability'].map(exp => (
                <Badge
                  key={exp}
                  variant={formData.expertise_areas?.includes(exp) ? 'default' : 'outline'}
                  className="cursor-pointer capitalize"
                  onClick={() => handleExpertiseToggle(exp)}
                >
                  {exp.replace('_', ' ')}
                </Badge>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Impact Management Tab */}
        <TabsContent value="impact" className="space-y-4 mt-4">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="impact_system"
                checked={formData.has_impact_measurement_system}
                onCheckedChange={(checked) => handleChange('has_impact_measurement_system', checked)}
              />
              <Label htmlFor="impact_system">{t('investor_form.impact_system')}</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="theory_of_change"
                checked={formData.has_theory_of_change}
                onCheckedChange={(checked) => handleChange('has_theory_of_change', checked)}
              />
              <Label htmlFor="theory_of_change">{t('investor_form.theory_of_change')}</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="impact_reports"
                checked={formData.publishes_impact_reports}
                onCheckedChange={(checked) => handleChange('publishes_impact_reports', checked)}
              />
              <Label htmlFor="impact_reports">{t('investor_form.impact_reports')}</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="third_party"
                checked={formData.third_party_verification}
                onCheckedChange={(checked) => handleChange('third_party_verification', checked)}
              />
              <Label htmlFor="third_party">{t('investor_form.third_party')}</Label>
            </div>
          </div>
        </TabsContent>

        {/* ESG Tab */}
        <TabsContent value="esg" className="space-y-4 mt-4">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="esg_policy"
                checked={formData.has_esg_policy}
                onCheckedChange={(checked) => handleChange('has_esg_policy', checked)}
              />
              <Label htmlFor="esg_policy">{t('investor_form.esg_policy')}</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="pri"
                checked={formData.signed_pri}
                onCheckedChange={(checked) => handleChange('signed_pri', checked)}
              />
              <Label htmlFor="pri">{t('investor_form.pri')}</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="esg_reports"
                checked={formData.publishes_esg_reports}
                onCheckedChange={(checked) => handleChange('publishes_esg_reports', checked)}
              />
              <Label htmlFor="esg_reports">{t('investor_form.esg_reports')}</Label>
            </div>
          </div>
        </TabsContent>

        {/* Historical Tab */}
        <TabsContent value="history" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('investor_form.previous_investments')}</Label>
              <Input
                type="number"
                value={formData.previous_investments_count}
                onChange={(e) => handleChange('previous_investments_count', e.target.value)}
                placeholder="10"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.total_capital_deployed')}</Label>
              <Input
                type="number"
                value={formData.total_capital_deployed}
                onChange={(e) => handleChange('total_capital_deployed', e.target.value)}
                placeholder="5000000"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.years_experience')}</Label>
              <Input
                type="number"
                value={formData.investment_period_years}
                onChange={(e) => handleChange('investment_period_years', e.target.value)}
                placeholder="5"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.beneficiaries_reached')}</Label>
              <Input
                type="number"
                value={formData.beneficiaries_reached}
                onChange={(e) => handleChange('beneficiaries_reached', e.target.value)}
                placeholder="10000"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('investor_form.jobs_created')}</Label>
              <Input
                type="number"
                value={formData.jobs_created}
                onChange={(e) => handleChange('jobs_created', e.target.value)}
                placeholder="100"
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <Label className="text-base font-semibold">{t('investor_form.transparency_section')}</Label>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="share_data"
                checked={formData.willing_to_share_data}
                onCheckedChange={(checked) => handleChange('willing_to_share_data', checked)}
              />
              <Label htmlFor="share_data">{t('investor_form.share_data')}</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="annual_reporting"
                checked={formData.annual_reporting}
                onCheckedChange={(checked) => handleChange('annual_reporting', checked)}
              />
              <Label htmlFor="annual_reporting">{t('investor_form.annual_reporting')}</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_public"
                checked={formData.is_public}
                onCheckedChange={(checked) => handleChange('is_public', checked)}
              />
              <Label htmlFor="is_public">Make profile public (visible to social actors)</Label>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          <X className="h-4 w-4 mr-1" />
          {t('investor_form.cancel')}
        </Button>
        <Button type="submit" disabled={loading} className="bg-emerald-700 hover:bg-emerald-800">
          {loading ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          {isEdit ? t('investor_form.update_profile') : t('investor_form.create_profile')}
        </Button>
      </div>
    </form>
  );
};

export default InvestorForm;
